/**
 * Reports landing page — feature cards linking to each report family.
 *
 * Two click behaviors per tile:
 *   1. `href` set    → navigate to the live report route
 *   2. No `href`     → open a preview Sheet (placeholder for upcoming reports)
 *
 * Tiles are RBAC-filtered: a tile only renders if the viewer holds at least
 * one of the `requires` permissions.
 */

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  AlertCircle,
  BarChart3,
  BookOpenCheck,
  Boxes,
  ClipboardList,
  Coins,
  CreditCard,
  FileText,
  Landmark,
  Package,
  Receipt,
  Scale,
  Ship,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  Button,
  Card,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useCurrentPermissions } from "../../../lib/permissions";

import type { PermissionKey } from "@bizop/shared/rbac";

export const Route = createFileRoute("/_app/reports/")({
  component: ReportsPage,
});

type ReportCard = {
  title: string;
  description: string;
  icon: LucideIcon;
  category:
    | "Sales"
    | "Finance"
    | "Inventory"
    | "Imports"
    | "People"
    | "Procurement";
  /**
   * If set, clicking the tile NAVIGATES to this route (a live report). When
   * omitted the tile opens the placeholder Sheet — used for reports still
   * being wired up.
   */
  href?: string;
  comingSoon?: boolean;
  /**
   * Permission(s) required to see this card. OR semantics for arrays.
   * Omit (or empty array) to always show.
   */
  requires?: PermissionKey | PermissionKey[];
};

const REPORTS: ReportCard[] = [
  /* ──────────────────────────── Sales ──────────────────────────── */
  {
    title: "Dealer Statement",
    description:
      "Per-dealer ledger: opening balance, invoices, payments, running balance. Printable, CSV export.",
    icon: FileText,
    category: "Sales",
    href: "/reports/dealer-statement",
    requires: "invoices.read",
  },
  {
    title: "Item Sales & Cost",
    description:
      "Per-SKU sales, COGS, gross margin and margin % for the chosen period.",
    icon: Receipt,
    category: "Sales",
    href: "/reports/item-sales-cost",
    requires: "reports.read",
  },
  {
    title: "Sales by Dealer",
    description: "Top dealers, tier mix, and territory contribution.",
    icon: BarChart3,
    category: "Sales",
    requires: "reports.read",
  },
  {
    title: "Points Liability",
    description:
      "Outstanding loyalty points and redemption velocity.",
    icon: Sparkles,
    category: "Sales",
    requires: "points.read",
  },

  /* ────────────────────────── Inventory ────────────────────────── */
  {
    title: "Product Stock, Sales & Cost",
    description:
      "Per-product stock level, sales over the period, unit cost and total COGS.",
    icon: Package,
    category: "Inventory",
    href: "/reports/product-stock-sales-cost",
    requires: "inventory.read",
  },
  {
    title: "Inventory Valuation",
    description:
      "WAC value by warehouse + slow-moving stock identification.",
    icon: Boxes,
    category: "Inventory",
    // WAC value = unit cost. Hide from anyone without finance.read.
    requires: "finance.read",
  },

  /* ─────────────────────────── Finance ─────────────────────────── */
  {
    title: "Income Statement",
    description:
      "Revenue, COGS, Gross Profit, OpEx and Net Income with prior-period delta.",
    icon: TrendingUp,
    category: "Finance",
    href: "/finance/reports/income-statement",
    requires: "finance.read",
  },
  {
    title: "Balance Sheet",
    description: "Assets, Liabilities and Equity as of a given date.",
    icon: Scale,
    category: "Finance",
    href: "/finance/reports/balance-sheet",
    requires: "finance.read",
  },
  {
    title: "Trial Balance",
    description:
      "Account-level debits/credits with period roll-forward.",
    icon: BookOpenCheck,
    category: "Finance",
    href: "/finance/reports/trial-balance",
    requires: "finance.read",
  },
  {
    title: "Cash Flow",
    description:
      "Inflow vs. outflow by payment method, with running balance.",
    icon: Wallet,
    category: "Finance",
    href: "/finance/reports/cash-flow",
    requires: "finance.read",
  },
  {
    title: "Receivables Aging",
    description:
      "Aged accounts receivable buckets with collector notes.",
    icon: AlertCircle,
    category: "Finance",
    // AR aging is what office managers chase — gate on invoices.read so
    // they see it without granting full finance.
    href: "/finance/reports/ar-aging",
    requires: "invoices.read",
  },
  {
    title: "Payment + Expense Ledger",
    description:
      "Unified payments-in, payments-out and expenses with date filter.",
    icon: Coins,
    category: "Finance",
    href: "/reports/payment-expense-ledger",
    requires: "finance.read",
  },
  {
    title: "Loan Schedule",
    description:
      "Forward-looking loan obligations across the next 12 months.",
    icon: CreditCard,
    category: "Finance",
    href: "/finance/loans",
    requires: "finance.read",
  },
  {
    title: "FX Exposure",
    description:
      "Open foreign-currency positions and unrealized P&L.",
    icon: BarChart3,
    category: "Finance",
    href: "/finance/fx",
    requires: "finance.read",
  },

  /* ────────────────────────── Procurement ──────────────────────── */
  {
    title: "Supplier Balances",
    description:
      "Per-supplier AP balance: opening, invoiced, paid, closing — with credit account view.",
    icon: Users,
    category: "Procurement",
    href: "/reports/supplier-balances",
    requires: "finance.read",
  },

  /* ─────────────────────────── Imports ─────────────────────────── */
  {
    title: "LC Pipeline",
    description:
      "Open Letters of Credit, drawdown progress, expiry alerts.",
    icon: Ship,
    category: "Imports",
    href: "/imports/lc",
    requires: "lc.read",
  },
  {
    title: "Shipment Performance",
    description: "ETA vs. actual arrival, landed-cost accuracy.",
    icon: ClipboardList,
    category: "Imports",
    requires: "shipments.read",
    comingSoon: true,
  },

  /* ─────────────────────────── People ─────────────────────────── */
  {
    title: "Headcount & Payroll",
    description:
      "Salary expense by branch with month-over-month change.",
    icon: BarChart3,
    category: "People",
    requires: "payroll.read",
    comingSoon: true,
  },
];

/** Pure helper — does the user's permission set allow this card? */
function isReportVisible(
  report: ReportCard,
  permissions: PermissionKey[],
): boolean {
  if (!report.requires) return true;
  const required = Array.isArray(report.requires)
    ? report.requires
    : [report.requires];
  return required.some((p) => permissions.includes(p));
}

const CATEGORY_ORDER: ReportCard["category"][] = [
  "Sales",
  "Inventory",
  "Finance",
  "Procurement",
  "Imports",
  "People",
];

function ReportsPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState<ReportCard | null>(null);

  // Hide reports the current role lacks permission to view BEFORE grouping,
  // so empty categories don't render an empty header.
  const permissions = useCurrentPermissions();
  const visible = REPORTS.filter((r) => isReportVisible(r, permissions));

  const grouped = visible.reduce<Record<string, ReportCard[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  function handleTileClick(report: ReportCard) {
    if (report.comingSoon) {
      setActive(report);
      return;
    }
    if (report.href) {
      void navigate({ to: report.href });
      return;
    }
    setActive(report);
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Reports"
        description="Pre-built reports across sales, inventory, finance, procurement, imports, and people. Click any tile to open."
      />

      {visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-6 py-10 text-center">
          <p className="text-[13px] text-(--text-secondary)">
            No reports are available for your role yet.
          </p>
          <p className="mt-1 text-[11px] text-(--text-tertiary)">
            Ask an admin to grant additional permissions if you need access
            to finance, inventory valuation, or payroll reports.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-6">
        {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((category) => {
          const items = grouped[category]!;
          return (
            <section key={category} className="flex flex-col gap-3">
              <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
                {category}
              </h2>
              <ul
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                role="list"
              >
                {items.map((r) => (
                  <ReportTile
                    key={r.title}
                    report={r}
                    onOpen={() => handleTileClick(r)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <Sheet
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) setActive(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {active ? (
            <ReportDetail
              report={active}
              onClose={() => setActive(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ReportTile({
  report,
  onOpen,
}: {
  report: ReportCard;
  onOpen: () => void;
}) {
  const Icon = report.icon;
  const isLive = Boolean(report.href) && !report.comingSoon;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
      >
        <Card
          variant="default"
          className={cn(
            "flex flex-col gap-3 transition-colors cursor-pointer",
            "hover:border-(--border-default)",
            report.comingSoon && "opacity-70",
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <span
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-md",
                "bg-(--accent-soft) text-(--accent)",
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="flex items-center gap-2">
              {isLive ? (
                <span className="rounded-full bg-(--success-soft) px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-(--success)">
                  Live
                </span>
              ) : null}
              {report.comingSoon ? (
                <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary) font-medium">
                  Soon
                </span>
              ) : (
                <ArrowRight className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
              )}
            </div>
          </div>
          <div>
            <h3 className="text-[14px] font-semibold tracking-tight text-(--text-primary)">
              {report.title}
            </h3>
            <p className="mt-1 text-[12px] text-(--text-secondary)">
              {report.description}
            </p>
          </div>
        </Card>
      </button>
    </li>
  );
}

function ReportDetail({
  report,
  onClose,
}: {
  report: ReportCard;
  onClose: () => void;
}) {
  const Icon = report.icon;
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-(--accent-soft) text-(--accent)">
            <Icon className="h-4 w-4" />
          </span>
          {report.title}
        </SheetTitle>
        <SheetDescription>{report.description}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Category
            </div>
            <div className="text-[13px] text-(--text-primary)">
              {report.category}
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Preview
            </div>
            <div className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-6 text-center">
              {report.comingSoon ? (
                <>
                  <div className="text-[13px] font-medium text-(--text-primary)">
                    Coming soon
                  </div>
                  <p className="mt-1 text-[12px] text-(--text-tertiary)">
                    This report is on the roadmap. We'll notify you when it
                    is available.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-[13px] font-medium text-(--text-primary)">
                    Live data wiring in progress
                  </div>
                  <p className="mt-1 text-[12px] text-(--text-tertiary)">
                    The {report.title.toLowerCase()} report will render here
                    once InstantDB queries are connected.
                  </p>
                </>
              )}
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              What you will see
            </div>
            <ul className="text-[13px] text-(--text-secondary) flex flex-col gap-1">
              <li>· Headline KPIs with delta vs. prior period</li>
              <li>· Time-series visualisation across the selected window</li>
              <li>· Detail table with row-level drill-down</li>
              <li>· Export to CSV / PDF</li>
            </ul>
          </section>
        </div>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {!report.comingSoon ? (
          <Button
            onClick={() => toast.info(`${report.title} export — coming soon`)}
          >
            Export
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}
